import { useEffect, useState, useRef } from 'react';
import { useFile } from '../../context/FileContext';
import { parseGitlabUrl, isValidGitlabUrl } from '../../utils/gitlabUrlParser';
import { selectDefaultBranch } from '../../constants/branchDefaults';
import { getGitlabSavedUrls, saveGitlabUrl, removeGitlabUrl } from '../../utils/gitlabUrlHistory';
import { getGitlabSavedToken, saveGitlabToken, clearGitlabToken } from '../../utils/gitlabTokenStorage';
import GitLabClient from '../../api/GitLabClient';
import { trackError } from '../../utils/analyticsEvents';

const GitLabConnector = () => {
  const {
    gitlabLink,
    setGitlabLink,
    gitlabBranch,
    setGitlabBranch,
    setGitlabNameAndRepo,
    gitlabAccessToken,
    setGitlabAccessToken,
    setGitlabBaseUrl,
    loadFile,
    setFocusedFilePath
  } = useFile();

  const [branches, setBranches] = useState([]);
  const [statusMessage, setStatusMessage] = useState('');
  const [savedUrls, setSavedUrls] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [rememberData, setRememberData] = useState(true);
  const [pendingFileToLoad, setPendingFileToLoad] = useState(null);
  const formRef = useRef(null);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const savedRemember = localStorage.getItem('gitlab_remember_data');
    if (savedRemember !== null) setRememberData(JSON.parse(savedRemember));
    setSavedUrls(getGitlabSavedUrls());
    const savedToken = getGitlabSavedToken();
    if (savedToken) setGitlabAccessToken(savedToken);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (gitlabLink === '' && gitlabAccessToken === '') return;
    setStatusMessage('');
    if (gitlabLink === '') return;

    if (!isValidGitlabUrl(gitlabLink)) {
      setStatusMessage('! Please enter a valid GitLab repository URL.');
      return;
    }

    let handler = setTimeout(async () => {
      try {
        const parsed = parseGitlabUrl(gitlabLink);
        if (!parsed) {
          setStatusMessage('! Invalid GitLab URL format.');
          return;
        }

        if (parsed.branch) setGitlabBranch(parsed.branch);
        setGitlabNameAndRepo(parsed.ownerRepo);
        setGitlabBaseUrl(parsed.baseUrl);

        const client = new GitLabClient(gitlabAccessToken, parsed.baseUrl);
        const branchNames = await client.getBranches(parsed.ownerRepo);
        setBranches(branchNames);
        setStatusMessage(`Found ${branchNames.length} branches.`);
        formRef.current?.requestSubmit();

        if (branchNames.length > 0) {
          if (!parsed.branch) {
            const selected = selectDefaultBranch(branchNames);
            if (selected) setGitlabBranch(selected);
          }

          if (parsed.filePath) {
            setFocusedFilePath(parsed.filePath);
            if (parsed.type === 'blob') {
              setPendingFileToLoad({
                name: parsed.filePath.split('/').pop(),
                path: parsed.filePath,
                type: 'blob'
              });
            } else {
              setPendingFileToLoad(null);
            }
          }

          if (rememberData) {
            const baseUrl = `https://gitlab.com${parsed.ownerRepo}`;
            saveGitlabUrl(baseUrl);
            setSavedUrls(getGitlabSavedUrls());
            saveGitlabToken(gitlabAccessToken);
          }
        }
      } catch (error) {
        setStatusMessage(`! Error: ${error.message}`);
        trackError('GitLab Load Error');
      }
    }, 500);

    return () => clearTimeout(handler);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gitlabLink, gitlabAccessToken]);

  useEffect(() => {
    if (pendingFileToLoad && branches.length > 0) {
      loadFile(pendingFileToLoad);
      setPendingFileToLoad(null);
    }
  }, [branches, pendingFileToLoad, loadFile]);

  useEffect(() => {
    if (showDropdown) {
      const handleClickOutside = (e) => {
        if (
          dropdownRef.current &&
          !dropdownRef.current.contains(e.target) &&
          !inputRef.current.contains(e.target)
        ) {
          setShowDropdown(false);
        }
      };
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showDropdown]);

  const selectUrl = (url) => {
    setGitlabLink(url);
    setShowDropdown(false);
  };

  const deleteUrl = (e, url) => {
    e.stopPropagation();
    removeGitlabUrl(url);
    setSavedUrls(getGitlabSavedUrls());
  };

  const deleteToken = () => {
    clearGitlabToken();
    setGitlabAccessToken('');
  };

  const handleRememberDataChange = (e) => {
    const value = e.target.checked;
    setRememberData(value);
    localStorage.setItem('gitlab_remember_data', JSON.stringify(value));
  };

  return (
    <div>
      <form ref={formRef} onSubmit={(e) => e.preventDefault()}>
        <h1>GitLab Repository URL</h1>
        <span>
          (paste a repository URL or a link to a specific file/folder to automatically expand and
          load it)
        </span>
        <div style={{ position: 'relative' }}>
          <input
            ref={inputRef}
            id="gitlab-repo-input"
            name="gitlab-repo-input"
            className="github-repo-input"
            autoComplete="off"
            type="text"
            placeholder="https://gitlab.com/<group>/<project>..."
            value={gitlabLink}
            onChange={(e) => setGitlabLink(e.target.value)}
            onFocus={() => { if (savedUrls.length > 0) setShowDropdown(true); }}
          />
          {showDropdown && savedUrls.length > 0 && (
            <div ref={dropdownRef} className="github-urls-dropdown">
              {savedUrls.map((url, idx) => (
                <div key={idx} onClick={() => selectUrl(url)} className="github-urls-item">
                  <div className="github-urls-text">{url}</div>
                  <button
                    onClick={(e) => deleteUrl(e, url)}
                    className="github-urls-delete-btn"
                    title="Delete this URL"
                  >
                    ✕
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
        <h2>GitLab Access Token</h2>
        <span>
          (optional - for private repositories and higher rate limits. GitLab &gt; User Settings &gt;
          Access Tokens &gt; create a token with <em>read_repository</em> scope)
        </span>
        <div style={{ position: 'relative' }}>
          <input
            className="github-repo-input"
            name="gitlab-token-input"
            autoComplete="off"
            type="text"
            placeholder="glpat-xxxxxxxxxxxxxxxxxxxx"
            value={gitlabAccessToken}
            onChange={(e) => setGitlabAccessToken(e.target.value)}
          />
          {gitlabAccessToken && (
            <button
              onClick={deleteToken}
              className="github-token-delete-btn"
              title="Delete saved token"
            >
              ✕
            </button>
          )}
        </div>
        <div className="github-remember-data-container">
          <input
            type="checkbox"
            id="gitlab-remember-data"
            className="github-remember-data-checkbox"
            checked={rememberData}
            onChange={handleRememberDataChange}
          />
          <label htmlFor="gitlab-remember-data">
            Remember this data locally in browser (URLs and token stored in your browser). Data are saved when repo successfully loads.
          </label>
        </div>
        <p style={{ color: statusMessage.startsWith('!') ? 'red' : 'black' }}>
          {statusMessage}
        </p>
        <button type="submit" style={{ display: 'none' }}>Submit</button>
      </form>
      <h2>Branch</h2>
      <select
        id="gitlab-branch-select"
        className="github-branch-select"
        value={gitlabBranch}
        onChange={(e) => setGitlabBranch(e.target.value)}
      >
        {branches.map((branch) => (
          <option key={branch} value={branch}>{branch}</option>
        ))}
      </select>
    </div>
  );
};

export default GitLabConnector;
